import React from 'react';
import Tag from '@hero/ui-kit/components/Tag';
import type { Block } from '../../types';
import { useEditorContext } from './EditorContext';
import { NESTING_LIMIT, SMS_ALLOWED_BLOCKS } from './constants';
import HeadingBlock from './blocks/HeadingBlock';
import ParagraphBlock from './blocks/ParagraphBlock';
import ButtonBlock from './blocks/ButtonBlock';
import ImageBlock from './blocks/ImageBlock';
import ListBlock from './blocks/ListBlock';
import DividerBlock from './blocks/DividerBlock';
import SpacerBlock from './blocks/SpacerBlock';
import TwoColumnBlock from './blocks/TwoColumnBlock';
import CardBlock from './blocks/CardBlock';
import TestimonialBlock from './blocks/TestimonialBlock';
import ConditionalBlock from './blocks/ConditionalBlock';

type Props = {
    blocks: Block[];
    depth?: number;
};

const BlockRenderer: React.FC<Props> = ({ blocks, depth = 0 }) => {
    const { channel } = useEditorContext();

    if (depth > NESTING_LIMIT) {
        return (
            <Tag type="warn" size="small">
                Nesting limit reached
            </Tag>
        );
    }

    return (
        <>
            {blocks.map((block, index) => {
                const isSmsInvalid =
                    channel === 'sms' &&
                    !SMS_ALLOWED_BLOCKS.some((allowedType) => allowedType === block.type);
                const rendered = renderBlock(block, depth);

                if (isSmsInvalid) {
                    return (
                        <div
                            key={index}
                            title="This block type is not supported in SMS templates"
                            style={{
                                border: '2px solid #dc3545',
                                borderRadius: '0.4rem',
                                padding: '0.4rem'
                            }}
                        >
                            {rendered}
                        </div>
                    );
                }

                return <React.Fragment key={index}>{rendered}</React.Fragment>;
            })}
        </>
    );
};

const renderBlock = (block: Block, depth: number): React.ReactNode => {
    switch (block.type) {
        case 'heading':
            return <HeadingBlock block={block} />;
        case 'paragraph':
            return <ParagraphBlock block={block} />;
        case 'button':
            return <ButtonBlock block={block} />;
        case 'image':
            return <ImageBlock block={block} />;
        case 'list':
            return <ListBlock block={block} />;
        case 'divider':
            return <DividerBlock />;
        case 'spacer':
            return <SpacerBlock block={block} />;
        case 'two_column':
            return <TwoColumnBlock block={block} depth={depth} />;
        case 'card':
            return <CardBlock block={block} depth={depth} />;
        case 'testimonial':
            return <TestimonialBlock block={block} />;
        case 'conditional':
            return <ConditionalBlock block={block} depth={depth} />;
        default:
            return null;
    }
};

export default BlockRenderer;
